import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import {
  createRelation,
  defaultRelationKind,
  deleteRelation,
  listRelations,
  relationAssigneeName,
  relationKindLabel,
  RELATION_KINDS,
  type RelationAssignee,
  type RelationDto,
  type RelationEnd,
  type RelationKind,
} from '../../lib/relations';
import { cn } from '../../lib/utils';
import { UserAvatar } from '../UserAvatar';
import ControlLink from './ControlLink';
import { StateLabel } from './table-cells';
import { useOverlays } from './overlay-context';

function otherEnd(row: RelationDto, workItemId: string): RelationEnd {
  return row.from.id === workItemId ? row.to : row.from;
}

function AssigneeFace({ person }: { person: RelationAssignee }) {
  const name = relationAssigneeName(person);
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex">
          <UserAvatar name={name} src={person.avatar_url} size={20} />
        </span>
      </TooltipTrigger>
      <TooltipContent>{name}</TooltipContent>
    </Tooltip>
  );
}

export type LinkedWorkItemCardModel = {
  id: string;
  key: string;
  title: string;
  project_id: string;
  archived_at: string | null;
  state: { name: string; color: string; group: string };
  assignees: RelationAssignee[];
};

export function LinkedWorkItemCard({ item }: { item: LinkedWorkItemCardModel }) {
  const settled = item.state.group === 'completed' || item.state.group === 'cancelled';
  return (
    <ControlLink
      workItemId={item.id}
      workItemKey={item.key}
      projectId={item.project_id}
      className="block min-w-0 flex-1 no-underline"
    >
      <div className="flex items-center gap-3 rounded-md bg-muted/70 px-3 py-2 shadow-[0_1px_2px_hsl(var(--foreground)/0.06)]">
        <span className="shrink-0 font-mono text-xs text-muted-foreground">{item.key}</span>
        <p
          className={cn(
            'min-w-0 flex-1 text-sm font-medium leading-snug',
            item.archived_at && 'line-through',
            (item.archived_at || settled) && 'text-muted-foreground',
          )}
        >
          {item.title}
        </p>
        <StateLabel name={item.state.name} color={item.state.color} group={item.state.group} />
        {item.assignees.length > 0 ? (
          <div className="flex shrink-0 items-center -space-x-1">
            {item.assignees.slice(0, 3).map((person) => (
              <AssigneeFace key={person.user_id} person={person} />
            ))}
          </div>
        ) : null}
      </div>
    </ControlLink>
  );
}

export default function WorkItemRelations({
  projectId,
  workItemId,
  workItemType,
  readOnly,
}: {
  projectId: string;
  workItemId: string;
  workItemType: string;
  readOnly: boolean;
}) {
  const { toast, confirm } = useOverlays();
  const [items, setItems] = useState<RelationDto[]>([]);
  const [kind, setKind] = useState<RelationKind>(defaultRelationKind(workItemType));
  const [key, setKey] = useState('');
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    try {
      setItems(await listRelations(projectId, workItemId));
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Failed to load links');
    }
  }, [projectId, workItemId, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    setKind(defaultRelationKind(workItemType));
  }, [workItemType]);

  async function onAdd(event: FormEvent) {
    event.preventDefault();
    const trimmed = key.trim();
    if (!trimmed) return;
    setBusy(true);
    try {
      await createRelation(projectId, workItemId, kind, trimmed);
      setKey('');
      setAdding(false);
      await load();
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Failed to link');
    } finally {
      setBusy(false);
    }
  }

  async function onRemove(row: RelationDto) {
    const other = otherEnd(row, workItemId);
    const ok = await confirm({
      title: 'Remove link',
      description: `Unlink ${other.key}?`,
      confirmLabel: 'Remove',
      destructive: true,
    });
    if (!ok) return;
    try {
      await deleteRelation(row.id);
      await load();
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Failed to unlink');
    }
  }

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-sm font-medium">Links</h2>
        {readOnly || adding ? null : (
          <Button type="button" size="sm" variant="outline" onClick={() => setAdding(true)}>
            Add a link
          </Button>
        )}
      </div>
      {items.length === 0 ? (
        adding ? null : <p className="text-sm text-muted-foreground">Nothing linked yet.</p>
      ) : (
        <ul className="space-y-1.5">
          {items.map((row) => {
            const other = otherEnd(row, workItemId);
            return (
              <li key={row.id} className="flex items-center gap-1">
                <LinkedWorkItemCard item={other} />
                {readOnly ? null : (
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7 shrink-0 text-muted-foreground"
                    onClick={() => void onRemove(row)}
                  >
                    <X className="h-3.5 w-3.5" aria-hidden />
                    <span className="sr-only">Remove link</span>
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {readOnly || !adding ? null : (
        <form onSubmit={(event) => void onAdd(event)} className="flex flex-wrap items-end gap-2">
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground" htmlFor={`rel-kind-${workItemId}`}>
              How it's linked
            </label>
            <Select value={kind} onValueChange={(value) => setKind(value as RelationKind)}>
              <SelectTrigger id={`rel-kind-${workItemId}`} className="h-8 w-36">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {RELATION_KINDS.map((option) => (
                  <SelectItem key={option} value={option}>
                    {relationKindLabel(option)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-[10rem] flex-1 space-y-1">
            <label className="text-xs text-muted-foreground" htmlFor={`rel-key-${workItemId}`}>
              Key of the other item
            </label>
            <Input
              id={`rel-key-${workItemId}`}
              value={key}
              onChange={(event) => setKey(event.target.value)}
              placeholder="e.g. PULSE-14"
              className="h-8"
              autoFocus
            />
          </div>
          <Button type="submit" size="sm" disabled={busy || key.trim().length === 0}>
            {busy ? 'Linking...' : 'Link'}
          </Button>
          <Button type="button" size="sm" variant="ghost" onClick={() => setAdding(false)}>
            Cancel
          </Button>
        </form>
      )}
    </section>
  );
}
